<template>
<div class="sce">
    <div class="sce-top">
        <div>
            <h3 class="mb-1 font-weight-bold text-body">{{ builder ? 'Home Page Builder' : (title || 'Website content') }}</h3>
            <p class="text-muted mb-0 sce-intro">{{ builder
                ? 'The sections of the homepage, in the order the site shows them. Drag the handle to move a section, use the switch to show or hide it, then press Save. To change a section\'s text or pictures, press Edit text.'
                : intro }}</p>
        </div>
        <div class="sce-top__actions">
            <span v-if="dirty" class="sce-unsaved">Unsaved changes</span>
            <a :href="previewUrl" target="_blank" rel="noopener" class="btn btn-light">Open the site</a>
            <button type="button" class="btn btn-primary" :disabled="!dirty || saving" @click="save">
                {{ saving ? 'Saving…' : 'Save' }}
            </button>
        </div>
    </div>

    <div v-if="!builder" class="sce-tips">
        <span><b>Red words:</b> put stars around them, e.g. <code>Lie down. *Pay less.* Sleep better.</code></span>
        <span><b>Hide a text:</b> empty the box.</span>
        <a href="javascript:void(0)" @click="showTokens = !showTokens"><b>Automatic values</b> (prices, phone…) {{ showTokens ? '▲' : '▼' }}</a>
        <div v-if="showTokens" class="sce-tokens">
            <p class="mb-2">Type one of these codes in any text and the site puts the real value in its place, and keeps it up to date. To write your own value instead, just type it (e.g. <code>from $999</code>).</p>
            <table>
                <tr v-for="t in tokens" :key="t.token">
                    <td><code>{{ t.token }}</code></td>
                    <td class="sce-tokens__val">{{ t.value || '(empty now)' }}</td>
                    <td class="text-muted">{{ t.about }}</td>
                </tr>
            </table>
        </div>
    </div>

    <div class="row">
        <div :class="showPreview ? 'col-xl-6' : 'col-12'">
            <draggable v-model="sections" handle=".sce-handle" :disabled="!orderable" @end="onReorder">
                <div v-for="(section, index) in sections" :key="section.key" class="sce-card" :class="{ 'is-open': openKey === section.key, 'is-off': !section.visible }">
                    <div class="sce-card__head" @click="toggle(section)">
                        <span v-if="orderable" class="sce-handle" title="Drag to move" @click.stop>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="9" cy="6" r="1.6"/><circle cx="15" cy="6" r="1.6"/><circle cx="9" cy="12" r="1.6"/><circle cx="15" cy="12" r="1.6"/><circle cx="9" cy="18" r="1.6"/><circle cx="15" cy="18" r="1.6"/></svg>
                        </span>
                        <span v-if="orderable" class="sce-num">{{ index + 1 }}</span>
                        <div class="sce-card__title">
                            <strong>{{ section.label }}</strong>
                            <small v-if="section.help">{{ section.help }}</small>
                        </div>
                        <div v-if="orderable" class="custom-control custom-switch sce-switch" @click.stop>
                            <input type="checkbox" class="custom-control-input" :id="'sceShow_' + section.key" v-model="section.visible" @change="markDirty">
                            <label class="custom-control-label" :for="'sceShow_' + section.key">{{ section.visible ? 'Shown' : 'Hidden' }}</label>
                        </div>
                        <router-link v-if="builder" :to="{ path: '/admin/content/homepage', query: { open: section.key } }" class="btn btn-sm btn-light text-nowrap" @click.native.stop>Edit text</router-link>
                        <svg v-if="!builder" class="sce-chev" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 9l6 6 6-6"/></svg>
                    </div>

                    <div v-if="openKey === section.key && !builder" class="sce-card__body">
                        <div v-if="section.manage.length" class="sce-manage">
                            <router-link v-for="m in section.manage" :key="m.to" :to="m.to" class="btn btn-sm btn-outline-primary">{{ m.label }} →</router-link>
                        </div>

                        <div v-for="field in section.fields" :key="field.key" class="sce-field">
                            <div class="sce-field__label">
                                <label :for="'f_' + field.key">{{ field.label }}</label>
                                <span v-if="isHidden(field)" class="sce-badge">Hidden on the site</span>
                                <a v-if="isChanged(field)" href="javascript:void(0)" class="sce-restore" @click="restore(field)">Restore original</a>
                            </div>

                            <input v-if="field.type === 'text' || field.type === 'url'" :id="'f_' + field.key" type="text" class="form-control"
                                :value="shown(field)" :maxlength="field.max" :list="field.type === 'url' ? 'sceUrls' : null"
                                :placeholder="field.type === 'url' ? '/shop, https://…, #showrooms' : ''" @input="set(field, $event.target.value)">

                            <textarea v-else-if="field.type === 'textarea' || field.type === 'lines'" :id="'f_' + field.key" class="form-control"
                                :rows="field.type === 'lines' ? 4 : 3" :value="shown(field)" :maxlength="field.max" @input="set(field, $event.target.value)"></textarea>

                            <input v-else-if="field.type === 'number'" :id="'f_' + field.key" type="number" class="form-control sce-num-input"
                                :min="field.min" :max="field.max" :value="shown(field)" @input="set(field, $event.target.value)">

                            <select v-else-if="field.type === 'select'" :id="'f_' + field.key" class="form-control sce-num-input" :value="shown(field)" @change="set(field, $event.target.value)">
                                <option v-for="(label, val) in field.options" :key="val" :value="val">{{ label }}</option>
                            </select>

                            <div v-else-if="field.type === 'image'" class="sce-image">
                                <img :src="imageUrl(shown(field))" alt="" @error="$event.target.style.opacity = .3">
                                <div>
                                    <button type="button" class="btn btn-sm btn-primary" @click="pickImage(field)">Choose a picture</button>
                                    <div class="text-muted small mt-1">{{ values[field.key] ? 'Your picture' : 'Original picture' }}</div>
                                </div>
                            </div>

                            <div v-else-if="field.type === 'links'" class="sce-links">
                                <div v-for="(link, i) in linkList(field)" :key="i" class="sce-link">
                                    <input type="text" class="form-control" placeholder="Text" maxlength="60" :value="link.label" @input="editLink(field, i, 'label', $event.target.value)">
                                    <input type="text" class="form-control" placeholder="/page or https://…" list="sceUrls" maxlength="500" :value="link.url" @input="editLink(field, i, 'url', $event.target.value)">
                                    <button type="button" class="btn btn-sm btn-light" title="Move up" :disabled="i === 0" @click="moveLink(field, i, -1)">↑</button>
                                    <button type="button" class="btn btn-sm btn-light" title="Move down" :disabled="i === linkList(field).length - 1" @click="moveLink(field, i, 1)">↓</button>
                                    <button type="button" class="btn btn-sm btn-light text-danger" title="Remove" @click="removeLink(field, i)">✕</button>
                                </div>
                                <button type="button" class="btn btn-sm btn-outline-secondary" @click="addLink(field)">+ Add a link</button>
                            </div>

                            <div v-else-if="field.type === 'media'" class="sce-media">
                                <div v-for="(item, i) in mediaList(field)" :key="i" class="sce-media__item">
                                    <div class="sce-media__head">
                                        <strong>Slide {{ i + 2 }} · {{ item.type === 'video' ? 'Video' : 'Photo' }}</strong>
                                        <span class="ml-auto">
                                            <button type="button" class="btn btn-sm btn-light" title="Move up" :disabled="i === 0" @click="moveMedia(field, i, -1)">↑</button>
                                            <button type="button" class="btn btn-sm btn-light" title="Move down" :disabled="i === mediaList(field).length - 1" @click="moveMedia(field, i, 1)">↓</button>
                                            <button type="button" class="btn btn-sm btn-light text-danger" title="Remove" @click="removeMedia(field, i)">✕</button>
                                        </span>
                                    </div>
                                    <div class="sce-image">
                                        <img v-if="item.image" :src="imageUrl(item.image)" alt="" @error="$event.target.style.opacity = .3">
                                        <div v-else class="sce-media__empty">{{ item.type === 'video' ? 'No cover' : 'No picture yet' }}</div>
                                        <div>
                                            <button type="button" class="btn btn-sm btn-primary" @click="pickMediaImage(field, i)">{{ item.image ? 'Change picture' : 'Choose a picture' }}</button>
                                            <a v-if="item.type === 'video' && item.image" href="javascript:void(0)" class="d-block small mt-1" @click="editMedia(field, i, 'image', '')">Remove cover</a>
                                            <div class="text-muted small mt-1">{{ item.type === 'video' ? 'Cover shown until the video starts (optional; the main photo is used if empty).' : 'Wide photo, at least 1600 px across.' }}</div>
                                        </div>
                                    </div>
                                    <div v-if="item.type === 'video'" class="mt-2">
                                        <textarea class="form-control" rows="2" placeholder="Video link from any site (https://…) or its embed code" maxlength="3000" :value="item.video" @input="editMedia(field, i, 'video', $event.target.value)"></textarea>
                                    </div>
                                </div>
                                <div class="sce-media__add">
                                    <button type="button" class="btn btn-sm btn-outline-secondary" @click="addMedia(field, 'image')">+ Add a photo</button>
                                    <button type="button" class="btn btn-sm btn-outline-secondary" @click="addMedia(field, 'video')">+ Add a video</button>
                                </div>
                            </div>

                            <small v-if="usesToken(field)" class="form-text sce-onsite">On the site: <b>{{ onSite(field) || '(nothing)' }}</b></small>
                            <small v-if="field.help" class="form-text text-muted">{{ field.help }}</small>
                            <small v-if="isChanged(field) && field.default && !['links', 'image', 'media'].includes(field.type)" class="form-text sce-original">Original: {{ field.default }}</small>
                        </div>
                        <p v-if="!section.fields.length" class="text-muted mb-0">This section has no text of its own.</p>
                    </div>
                </div>
            </draggable>
        </div>

        <div v-if="showPreview" class="col-xl-6 d-none d-xl-block">
            <div class="sce-preview">
                <div class="sce-preview__bar">
                    <strong>Live site</strong>
                    <span class="text-muted small">Updates after you save</span>
                    <div class="btn-group btn-group-sm ml-auto">
                        <button type="button" class="btn" :class="device === 'desktop' ? 'btn-dark' : 'btn-light'" @click="device = 'desktop'">Computer</button>
                        <button type="button" class="btn" :class="device === 'mobile' ? 'btn-dark' : 'btn-light'" @click="device = 'mobile'">Phone</button>
                    </div>
                </div>
                <div ref="frameBox" class="sce-preview__frame" :class="'is-' + device">
                    <iframe ref="frame" :src="frameSrc" :style="frameStyle" title="Site preview" @load="onFrameLoad"></iframe>
                </div>
            </div>
        </div>
    </div>

    <datalist id="sceUrls">
        <option v-for="u in urlSuggestions" :key="u.url" :value="u.url">{{ u.label }}</option>
    </datalist>
    <attach-image @toggleImageSelect="showPicker = !showPicker" :showModal="showPicker" @setImage="setImage"/>
</div>
</template>

<script>
import draggable from 'vuedraggable';

const STATIC_URLS = [
    { url: '#showrooms', label: 'Opens the showroom list' },
    { url: '#chat', label: 'Opens the chat' },
    { url: '/shop', label: 'Shop' },
    { url: '/clearance', label: 'Clearance' },
    { url: '/quiz', label: 'Sleep quiz' },
    { url: '/track_order', label: 'Track order' },
    { url: '/blog', label: 'Blog' },
];

export default {
    components: { draggable },
    props: {
        group: { type: String, required: true },
        // Home Page Builder (/admin/home-setting): only order + show/hide, no text fields
        builder: { type: Boolean, default: false },
    },

    data() {
        return {
            title: '', intro: '', previewUrl: '/', orderable: false, tokens: [], showTokens: false,
            sections: [], values: {}, original: {}, originalOrder: '', originalVisible: '',
            links: {}, openKey: null, dirty: false, saving: false,
            showPicker: false, pickerField: null, pickerIndex: null,
            device: 'desktop', frameSrc: '', frameWidth: 600, pageUrls: [],
        };
    },

    computed: {
        showPreview() { return this.group !== '' && window.innerWidth >= 1200; },
        frameStyle() {
            const w = this.device === 'mobile' ? 390 : 1360;
            const scale = Math.min(1, this.frameWidth / w);
            return { width: w + 'px', height: Math.round(760 / scale) + 'px', transform: 'scale(' + scale + ')' };
        },
        urlSuggestions() { return STATIC_URLS.concat(this.pageUrls); },
    },

    watch: {
        group() { this.load(); },
        // Home Page Builder -> "Edit text" reuses this component on another route
        '$route'() { this.load(); },
        device() { this.$nextTick(this.measure); },
    },

    methods: {
        auth() { return { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }; },

        load() {
            this.$parent.loading = true;
            this.openKey = null;
            axios.get('/api/admin/site-content/' + this.group, this.auth())
                .then(res => {
                    const d = res.data.data;
                    this.title = d.title; this.intro = d.intro; this.previewUrl = d.preview;
                    this.orderable = d.orderable; this.tokens = d.tokens;
                    const values = {};
                    d.sections.forEach(s => s.fields.forEach(f => { values[f.key] = f.value; }));
                    this.values = values;
                    this.original = Object.assign({}, values);
                    this.links = {};
                    this.sections = d.sections;
                    this.originalOrder = this.orderKey();
                    this.originalVisible = this.visibleKey();
                    this.dirty = false;
                    this.frameSrc = this.previewUrl + '?preview=' + Date.now();
                    // "Edit text" from the Home Page Builder opens that section here
                    const open = this.$route.query.open;
                    if (open && this.sections.some(sec => sec.key === open)) {
                        this.openKey = open;
                        this.$nextTick(() => {
                            const el = this.$el.querySelector('.sce-card.is-open');
                            if (el) el.scrollIntoView({ block: 'start', behavior: 'smooth' });
                        });
                    }
                })
                .catch(() => this.$toaster.error('Could not load the content. Please refresh the page.'))
                .finally(() => (this.$parent.loading = false));

            axios.get('/api/admin/site-pages', this.auth())
                .then(res => { this.pageUrls = res.data.data.map(p => ({ url: p.url, label: p.title })); })
                .catch(() => {});
        },

        orderKey() { return this.sections.map(s => s.key).join(','); },
        visibleKey() { return this.sections.map(s => s.key + ':' + (s.visible ? 1 : 0)).join(','); },

        toggle(section) {
            this.openKey = this.openKey === section.key ? null : section.key;
            if (this.openKey) this.scrollPreview(section);
        },

        // What the box shows: the saved text, or the original when it was never changed.
        shown(field) {
            const v = this.values[field.key];
            return v === null || v === undefined ? field.default : v;
        },
        set(field, value) {
            this.$set(this.values, field.key, value);
            this.markDirty();
        },
        restore(field) {
            this.$set(this.values, field.key, null);
            this.$delete(this.links, field.key);
            this.markDirty();
        },
        isChanged(field) {
            const v = this.values[field.key];
            return v !== null && v !== undefined && v !== field.default;
        },
        usesToken(field) {
            return ['text', 'textarea'].includes(field.type) && /\{[a-z_0-9]+\}/.test(String(this.shown(field) || ''));
        },
        // The text as visitors will read it: codes replaced by their current value, *stars* removed.
        onSite(field) {
            let text = String(this.shown(field) || '');
            this.tokens.forEach(t => { text = text.split(t.token).join(t.value || ''); });
            return text.replace(/\*/g, '').trim();
        },
        isHidden(field) {
            return field.empty === 'hide' && String(this.shown(field)).trim() === '' && field.type !== 'url';
        },
        markDirty() {
            this.dirty = this.sections.some(s => s.fields.some(f => this.values[f.key] !== this.original[f.key]))
                || this.orderKey() !== this.originalOrder || this.visibleKey() !== this.originalVisible;
        },
        onReorder() { this.markDirty(); },

        imageUrl(path) {
            if (!path) return '';
            if (/^(https?:)?\/\//i.test(path)) return path;
            if (/^\/?(images|assets)\//.test(path)) return '/' + path.replace(/^\//, '');
            return this.cdnUrl(path);
        },
        pickImage(field) { this.pickerField = field; this.pickerIndex = null; this.showPicker = true; },
        setImage(gallary) {
            if (!this.pickerField || !gallary) return;
            if (this.pickerIndex !== null) this.editMedia(this.pickerField, this.pickerIndex, 'image', gallary.gallary_path);
            else this.set(this.pickerField, gallary.gallary_path);
        },

        // 'media' fields: extra banner slides, each a photo or a video (+ optional cover photo).
        mediaList(field) {
            if (!this.links[field.key]) {
                let list = [];
                try { list = JSON.parse(this.shown(field) || '[]'); } catch (e) { list = []; }
                this.$set(this.links, field.key, Array.isArray(list)
                    ? list.map(m => ({ type: m.type === 'video' ? 'video' : 'image', image: m.image || '', video: m.video || '' }))
                    : []);
            }
            return this.links[field.key];
        },
        commitMedia(field) {
            this.set(field, JSON.stringify(this.links[field.key].map(m => m.type === 'video'
                ? { type: 'video', video: m.video, image: m.image }
                : { type: 'image', image: m.image })));
        },
        editMedia(field, i, prop, value) { this.mediaList(field)[i][prop] = value; this.commitMedia(field); },
        addMedia(field, type) {
            this.mediaList(field).push({ type, image: '', video: '' });
            this.commitMedia(field);
            if (type === 'image') this.pickMediaImage(field, this.links[field.key].length - 1);
        },
        removeMedia(field, i) { this.links[field.key].splice(i, 1); this.commitMedia(field); },
        moveMedia(field, i, dir) {
            const list = this.links[field.key];
            const item = list.splice(i, 1)[0];
            list.splice(i + dir, 0, item);
            this.commitMedia(field);
        },
        pickMediaImage(field, i) { this.pickerField = field; this.pickerIndex = i; this.showPicker = true; },

        linkList(field) {
            if (!this.links[field.key]) {
                let list = [];
                try { list = JSON.parse(this.shown(field) || '[]'); } catch (e) { list = []; }
                this.$set(this.links, field.key, Array.isArray(list) ? list.map(l => ({ label: l.label || '', url: l.url || '' })) : []);
            }
            return this.links[field.key];
        },
        commitLinks(field) { this.set(field, JSON.stringify(this.links[field.key])); },
        editLink(field, i, prop, value) { this.links[field.key][i][prop] = value; this.commitLinks(field); },
        addLink(field) { this.linkList(field).push({ label: '', url: '' }); this.commitLinks(field); },
        removeLink(field, i) { this.links[field.key].splice(i, 1); this.commitLinks(field); },
        moveLink(field, i, dir) {
            const list = this.links[field.key];
            const item = list.splice(i, 1)[0];
            list.splice(i + dir, 0, item);
            this.commitLinks(field);
        },

        save() {
            const values = {};
            this.sections.forEach(s => s.fields.forEach(f => {
                if (this.values[f.key] !== this.original[f.key]) values[f.key] = this.values[f.key];
            }));
            const payload = { values };
            if (this.orderable) {
                payload.order = this.sections.map(s => s.key);
                payload.visible = {};
                this.sections.forEach(s => { payload.visible[s.key] = s.visible; });
            }
            this.saving = true;
            axios.put('/api/admin/site-content/' + this.group, payload, this.auth())
                .then(res => {
                    this.$toaster.success(res.data.message || 'Saved');
                    this.original = Object.assign({}, this.values);
                    this.originalOrder = this.orderKey();
                    this.originalVisible = this.visibleKey();
                    this.dirty = false;
                    this.frameSrc = this.previewUrl + '?preview=' + Date.now();
                })
                .catch(err => {
                    const d = err.response && err.response.data;
                    this.$toaster.error((d && d.message) || 'Could not save. Please try again.');
                    if (d && d.field) {
                        const section = this.sections.find(s => s.fields.some(f => f.key === d.field));
                        if (section) this.openKey = section.key;
                    }
                })
                .finally(() => (this.saving = false));
        },

        measure() {
            if (this.$refs.frameBox) this.frameWidth = this.$refs.frameBox.clientWidth;
        },
        onFrameLoad() {
            this.measure();
            const section = this.sections.find(s => s.key === this.openKey);
            if (section) this.scrollPreview(section);
        },
        // Same-origin preview: jump to the section being edited.
        scrollPreview(section) {
            const frame = this.$refs.frame;
            if (!frame) return;
            try {
                const win = frame.contentWindow;
                const doc = frame.contentDocument;
                if (section.preview === 'bottom') { win.scrollTo(0, doc.body.scrollHeight); return; }
                if (section.preview === 'top' || !this.orderable) { win.scrollTo(0, 0); return; }
                const el = doc.querySelector('[data-home-section="' + section.key + '"]');
                if (el) win.scrollTo(0, Math.max(0, el.getBoundingClientRect().top + win.pageYOffset - 20));
            } catch (e) { /* preview not loaded yet */ }
        },
        beforeUnload(e) {
            if (this.dirty) { e.preventDefault(); e.returnValue = ''; }
        },
    },

    beforeRouteUpdate(to, from, next) {
        if (this.dirty && !window.confirm('You have unsaved changes. Leave without saving?')) return next(false);
        next();
    },
    beforeRouteLeave(to, from, next) {
        if (this.dirty && !window.confirm('You have unsaved changes. Leave without saving?')) return next(false);
        next();
    },

    mounted() {
        this.load();
        window.addEventListener('resize', this.measure);
        window.addEventListener('beforeunload', this.beforeUnload);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.measure);
        window.removeEventListener('beforeunload', this.beforeUnload);
    },
};
</script>

<style scoped>
.sce { padding: 0 4px 60px; }
.sce-top { display: flex; flex-wrap: wrap; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 14px; }
.sce-intro { max-width: 760px; }
.sce-top__actions { display: flex; align-items: center; gap: 10px; position: sticky; top: 70px; }
.sce-unsaved { color: #b0260a; font-weight: 600; font-size: 13px; }
.sce-tips { display: flex; flex-wrap: wrap; gap: 8px 22px; background: #fff; border: 1px solid #ebedf3; border-radius: 8px; padding: 10px 14px; margin-bottom: 16px; font-size: 13px; color: #555; }
.sce-tips code { margin-right: 6px; }
.sce-tokens { flex-basis: 100%; background: #f7f8fa; border-radius: 8px; padding: 10px 12px; }
.sce-tokens table { width: 100%; }
.sce-tokens td { padding: 4px 10px 4px 0; vertical-align: top; }
.sce-tokens__val { font-weight: 700; white-space: nowrap; color: #111; }
.sce-onsite { color: #23804a; }
.sce-card { background: #fff; border: 1px solid #ebedf3; border-radius: 10px; margin-bottom: 10px; transition: box-shadow .15s; }
.sce-card.is-open { box-shadow: 0 4px 18px rgba(0,0,0,.07); border-color: #c9ccd6; }
.sce-card.is-off .sce-card__title strong { color: #999; text-decoration: line-through; }
.sce-card__head { display: flex; align-items: center; gap: 12px; padding: 14px 16px; cursor: pointer; }
.sce-handle { cursor: grab; color: #a0a4b0; display: flex; padding: 4px; }
.sce-num { width: 26px; height: 26px; border-radius: 50%; background: #f3f4f7; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; color: #555; flex-shrink: 0; }
.sce-card__title { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.sce-card__title strong { font-size: 15px; color: #111; }
.sce-card__title small { color: #888; font-size: 12.5px; line-height: 1.4; }
.sce-switch { flex-shrink: 0; min-width: 96px; }
.sce-chev { color: #999; transition: transform .2s; flex-shrink: 0; }
.sce-card.is-open .sce-chev { transform: rotate(180deg); }
.sce-card__body { border-top: 1px solid #f0f1f4; padding: 16px 18px 6px; }
.sce-manage { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; padding: 10px 12px; background: #f7f8fa; border-radius: 8px; }
.sce-field { margin-bottom: 18px; }
.sce-field__label { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; }
.sce-field__label label { margin: 0; font-weight: 600; color: #333; }
.sce-badge { background: #fdecea; color: #b0260a; font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 20px; }
.sce-restore { margin-left: auto; font-size: 12.5px; }
.sce-original { color: #8a8f99; font-style: italic; }
.sce-num-input { max-width: 220px; }
.sce-image { display: flex; align-items: center; gap: 14px; }
.sce-image img { width: 150px; height: 90px; object-fit: cover; border-radius: 8px; background: #f3f4f7; border: 1px solid #ebedf3; }
.sce-links { display: flex; flex-direction: column; gap: 8px; align-items: flex-start; }
.sce-media { display: flex; flex-direction: column; gap: 12px; }
.sce-media__item { border: 1px solid #e3e6ec; border-radius: 8px; padding: 12px; background: #fafbfc; }
.sce-media__head { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
.sce-media__empty { width: 150px; height: 90px; border-radius: 6px; border: 1px dashed #c8ccd4; display: flex; align-items: center; justify-content: center; color: #8a909c; font-size: 12px; flex-shrink: 0; }
.sce-media__add { display: flex; gap: 8px; flex-wrap: wrap; }
.sce-link { display: flex; gap: 6px; width: 100%; }
.sce-link input:first-child { flex: 0 0 38%; }
.sce-link input { flex: 1; min-width: 0; }
.sce-preview { position: sticky; top: 80px; background: #fff; border: 1px solid #ebedf3; border-radius: 10px; overflow: hidden; }
.sce-preview__bar { display: flex; align-items: center; gap: 10px; padding: 10px 14px; border-bottom: 1px solid #ebedf3; }
.sce-preview__frame { height: 760px; overflow: hidden; background: #f3f4f7; position: relative; }
.sce-preview__frame iframe { border: 0; transform-origin: 0 0; background: #fff; display: block; }
.sce-preview__frame.is-mobile { display: flex; justify-content: center; }
.sce-preview__frame.is-mobile iframe { transform-origin: 50% 0; }
</style>
